import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo, type FormEvent } from "react";
import { Mail, Leaf, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import heroAsset from "@/assets/speaker-hero-v2.webp.asset.json";
import {
  getCurrentEventLabel,
  getMillisecondsUntilNextEventDateChange,
  getNextClassDateIso,
} from "@/lib/whatsapp-groups";
const heroImage = heroAsset.url;

const pad = (n: number) => n.toString().padStart(2, "0");

function useCountdown(targetIso: string | null) {
  const target = useMemo(
    () => (targetIso ? new Date(targetIso).getTime() : null),
    [targetIso],
  );
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const ready = now !== null && target !== null;
  const diff = ready ? Math.max(0, target - now) : 0;
  const s = Math.floor(diff / 1000);
  return {
    ready,
    started: ready && diff === 0,
    urgent: ready && diff > 0 && diff < 3600000,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };

}


const TRACK_SCRIPT_BASE = "https://kegqh.ttrk.io/track.js";
const TRACK_CMP_ID = "6a321a03014cc93d3a537df0";
const PAGEVIEW_POSTBACK_BASE = "https://kegqh.ttrk.io/postback";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}
function setCookie(name: string, value: string, maxAgeSec: number) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSec}; SameSite=Lax`;
}
function generateEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function getOrCreateExternalId(): string {
  if (typeof window === "undefined") return "";
  const KEY = "lvbl_eid";
  let id = "";
  try { id = window.localStorage.getItem(KEY) || ""; } catch { /* ignore */ }
  if (!id) id = getCookie(KEY) || "";
  if (!id) {
    id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  try { window.localStorage.setItem(KEY, id); } catch { /* ignore */ }
  setCookie(KEY, id, 60 * 60 * 24 * 365);
  return id;
}
async function getClickIdWithRetry(maxWaitMs = 1500): Promise<{ clickid: string; source: string }> {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    const fromStorage = window.localStorage.getItem("clickid");
    if (fromStorage) return { clickid: fromStorage, source: "localStorage" };
    const btn = document.querySelector<HTMLAnchorElement>('a[href*="/click"]');
    if (btn?.href && btn.href.includes("clickid=")) {
      try {
        const u = new URL(btn.href);
        const c = u.searchParams.get("clickid");
        if (c) {
          window.localStorage.setItem("clickid", c);
          return { clickid: c, source: "button" };
        }
      } catch { /* ignore */ }
    }
    await new Promise((r) => setTimeout(r, 200));
  }
  return { clickid: "", source: "none" };
}

export const Route = createFileRoute("/inscricao")({
  head: () => ({
    meta: [
      { title: "Inscrição — Cura pela Alimentação" },
      {
        name: "description",
        content:
          "Masterclass gratuita e ao vivo: descubra como ativar a capacidade natural de cura do seu corpo pela alimentação.",
      },
      { property: "og:title", content: "Cura pela Alimentação — Inscrição" },
      {
        property: "og:description",
        content: "Inscreva-se gratuitamente na masterclass ao vivo do Dr. Eduardo Corassa.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Index,
});

function Index() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eventLabel, setEventLabel] = useState(() => getCurrentEventLabel());
  const [classIso, setClassIso] = useState<string | null>(null);
  const cd = useCountdown(classIso);
  const classDateLabel = useMemo(() => {
    if (!classIso) return "";
    const d = new Date(classIso);
    const day = String(d.getUTCDate()).padStart(2, "0");
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  }, [classIso]);

  useEffect(() => {
    setEventLabel(getCurrentEventLabel());
    setClassIso(getNextClassDateIso());
    const t = window.setTimeout(() => {
      setEventLabel(getCurrentEventLabel());
      setClassIso(getNextClassDateIso());
    }, getMillisecondsUntilNextEventDateChange() + 100);
    return () => window.clearTimeout(t);
  }, [eventLabel]);



  // Tracking bootstrap: track.js + clickid capture + PageView postback
  useEffect(() => {
    if (typeof window === "undefined") return;

    void import("@/lib/visitor-identity").then((m) => m.resolveFbClick());
    getOrCreateExternalId();

    const loadTrackingScript = (attempt = 1, max = 3): void => {
      const existing = document.querySelector('script[src*="kegqh.ttrk.io/track.js"]');
      if (existing) existing.remove();
      const s = document.createElement("script");
      s.src = `${TRACK_SCRIPT_BASE}?rtkcmpid=${TRACK_CMP_ID}&t=${Date.now()}`;
      s.async = true;
      s.onerror = () => {
        if (attempt < max) setTimeout(() => loadTrackingScript(attempt + 1, max), 2000);
      };
      document.head.appendChild(s);
    };
    // Carrega fora do caminho crítico (após o load / idle) para não atrasar o LCP
    const startLoad = () => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
      if (ric) ric(() => loadTrackingScript(), { timeout: 2000 });
      else window.setTimeout(() => loadTrackingScript(), 200);
    };
    if (document.readyState === "complete") startLoad();
    else window.addEventListener("load", startLoad, { once: true });

    let captured = false;
    const processAnchor = (a: HTMLAnchorElement) => {
      if (captured || !a.href || !a.href.includes("clickid=")) return;
      try {
        const u = new URL(a.href);
        const c = u.searchParams.get("clickid");
        if (c) {
          window.localStorage.setItem("clickid", c);
          captured = true;
        }
      } catch { /* ignore */ }
    };
    const scan = () => {
      if (captured) return;
      document.querySelectorAll<HTMLAnchorElement>('a[href*="/click"]').forEach(processAnchor);
    };
    scan();
    const obs = new MutationObserver(scan);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["href"] });
    let n = 0;
    const scanInt = window.setInterval(() => {
      if (captured || ++n > 40) window.clearInterval(scanInt);
      else scan();
    }, 500);

    const pbInt = window.setInterval(() => {
      const c = window.localStorage.getItem("clickid");
      if (c) {
        fetch(`${PAGEVIEW_POSTBACK_BASE}?type=PageView&clickid=${encodeURIComponent(c)}`).catch(() => {});
        window.clearInterval(pbInt);
      }
    }, 500);

    return () => {
      document.querySelectorAll('script[src*="kegqh.ttrk.io/track.js"]').forEach((s) => s.remove());
      obs.disconnect();
      window.clearInterval(scanInt);
      window.clearInterval(pbInt);
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!valid) {
      setError("Digite um e-mail válido para garantir sua vaga.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      // 1) clickid: URL params -> retry storage
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("clickid") || params.get("click_id") || params.get("xcod");
      let clickid = "";
      let clickidSource = "none";
      if (fromUrl) {
        clickid = fromUrl;
        clickidSource = "url";
        window.localStorage.setItem("clickid", fromUrl);
      } else {
        const got = await getClickIdWithRetry(1500);
        clickid = got.clickid;
        clickidSource = got.source;
      }

      // 2) Meta Pixel Lead com eventID p/ dedup com CAPI
      const eventId = generateEventId();
      const fbp = getCookie("_fbp");
      const { saveVisitorIdentity, resolveFbClick, applyAdvancedMatching } = await import(
        "@/lib/visitor-identity"
      );
      const fbClick = resolveFbClick();
      const fbc = fbClick.fbc;
      const externalId = getOrCreateExternalId();
      // guarda a identidade p/ advanced matching em todos os eventos seguintes
      saveVisitorIdentity({ email: trimmed });
      applyAdvancedMatching();
      try {
        if (typeof window.fbq === "function") {
          window.fbq(
            "track",
            "Lead",
            {
              content_name: "Doutor Eduardo Corassa",
            },
            { eventID: eventId },
          );
        }
      } catch { /* ignore */ }

      // 3) Salva no backend (fire-and-forget com keepalive)
      fetch("/api/public/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          clickid: clickid || null,
          clickid_source: clickidSource !== "none" ? clickidSource : null,
          meta_event_id: eventId,
          fbp,
          fbc,
          external_id: externalId,
          fbclid: fbClick.fbclid,
        }),
        keepalive: true,
      }).catch((err) => console.error("[CAPTURA] erro fetch:", err));

      setSuccess(true);
      navigate({ to: "/obrigado" });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível registrar sua inscrição. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen flex-col bg-deep text-cream">
      <a href="https://kegqh.ttrk.io/click" className="hidden" aria-hidden="true" tabIndex={-1}>
        <button type="button" tabIndex={-1}>Track</button>
      </a>

      <section className="flex min-h-[100dvh] flex-col">
        <div className="anim-fade-up relative h-[34dvh] min-h-[230px] max-h-[390px] w-full overflow-hidden sm:h-[42dvh]">
          <img
            src={heroImage}
            alt="Dr. Eduardo Corassa apresentando a masterclass Cura pela Alimentação"
            width={832}
            height={704}
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-deep" aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-3 px-5 text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">Masterclass ao vivo e gratuita</p>
            <p className="mt-1 font-display text-2xl font-bold leading-tight text-gold sm:text-3xl">Cura pela Alimentação</p>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-[640px] flex-1 flex-col items-center px-5 pb-5 pt-3 text-center">
          <div className="anim-fade-up anim-delay-150 inline-flex items-center gap-2 rounded-full border border-cream/20 bg-card/70 px-3 py-1.5">
            <span className="live-dot size-2 rounded-full bg-leaf-bright" />
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-cream">QUASE LÁ · {eventLabel}</span>
          </div>

          <h1 className="anim-fade-up anim-delay-300 mt-3 max-w-[600px] font-display text-[1.65rem] font-bold uppercase leading-[1.08] text-cream sm:text-4xl">
            E se a cura nunca esteve na <span className="italic text-gold">farmácia</span>?
          </h1>

          <p className="anim-fade-up anim-delay-450 mt-2 max-w-[560px] text-[13px] leading-relaxed text-cream/75 sm:text-base">
            Seu corpo sabe se curar sozinho: essa descoberta rendeu o Nobel de Medicina em 2016. Nesta aula você aprende a religar esse poder pela alimentação.
          </p>

          <div className="anim-fade-up anim-delay-600 mt-4 w-full max-w-[460px] rounded-2xl bg-cream p-4 text-left shadow-2xl sm:p-5">
            <form onSubmit={onSubmit} noValidate>
              <label htmlFor="inscricao-email" className="block text-sm font-bold text-deep">Garanta sua vaga gratuita:</label>
              <div className="relative mt-2">
                <Mail className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-deep/45" strokeWidth={1.8} />
                <input
                  id="inscricao-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  maxLength={120}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
                  onBlur={() => {
                    const value = email.trim();
                    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) setError("Confira seu e-mail — parece estar incompleto.");
                  }}
                  placeholder="Insira seu melhor e-mail"
                  aria-invalid={!!error}
                  aria-describedby={error ? "inscricao-email-error" : undefined}
                  className="min-h-12 w-full rounded-lg border border-deep/15 bg-background/5 py-3 pl-11 pr-4 text-base text-deep outline-none placeholder:text-deep/40 focus:border-gold focus:ring-2 focus:ring-gold/30"
                />
              </div>
              {error && <p id="inscricao-email-error" className="mt-2 text-xs font-medium text-red-700">{error}</p>}
              <button
                type="submit"
                disabled={loading || success}
                aria-busy={loading || undefined}
                className="mt-3 flex min-h-13 w-full items-center justify-center gap-2 rounded-lg bg-gold px-4 text-sm font-bold uppercase text-deep transition hover:brightness-110 active:scale-[0.99] disabled:opacity-70 sm:text-base"
              >
                {loading ? <><Loader2 className="size-5 animate-spin" /> Garantindo sua vaga…</> : <>Quero minha vaga gratuita →</>}
              </button>
              <p className="mt-2 text-center text-[11px] font-medium text-deep/65">🔒 Seus dados estão 100% seguros.</p>
            </form>
          </div>

          <div className="anim-fade-up anim-delay-750 mt-4 w-full max-w-[460px]">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cream/65">A aula começa em:</p>
            <div className="mt-2 flex min-h-[62px] items-start justify-center gap-2" aria-live="polite">
              {cd.ready && cd.started ? (
                <p className="font-display text-lg font-bold text-gold">A aula está começando agora</p>
              ) : ([['dias', cd.days], ['horas', cd.hours], ['min', cd.minutes], ['seg', cd.seconds]] as const).map(([label, value]) => (
                <div key={label} className="min-w-[58px] rounded-md bg-card px-2 py-2 text-center sm:min-w-[66px]">
                  <div className="font-display text-xl font-bold tabular-nums text-cream sm:text-2xl">{cd.ready ? pad(value) : '--'}</div>
                  <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-cream/55">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-cream/10 bg-card/70 px-5 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.1em] text-cream/60">
        Dr. Eduardo Corassa <span className="mx-2 text-gold">·</span> Masterclass 100% gratuita
      </div>
    </main>
  );
}

function CountdownPanel({ cd }: { cd: ReturnType<typeof useCountdown> }) {
  const units = [
    ["dias", cd.days],
    ["horas", cd.hours],
    ["min", cd.minutes],
    ["seg", cd.seconds],
  ] as const;

  return (
    <div className="anim-fade-up anim-delay-350 mt-4 w-full">
      <div
        className={`rounded-3xl border bg-deep/60 px-3 py-3.5 backdrop-blur-sm ${
          cd.urgent ? "urgent-glow border-gold/60" : "border-gold/30"
        }`}
        style={{ boxShadow: "0 18px 40px -20px oklch(0 0 0 / 0.7)" }}
      >
        <p className="text-center text-[10px] font-bold uppercase tracking-[0.25em] text-gold">
          A aula começa em
        </p>
        <div
          className="mt-2.5 flex items-start justify-center gap-2 sm:gap-3"
          style={{ minHeight: 62 }}
          aria-live="polite"
        >
          {cd.ready && cd.started ? (
            <p className="font-display text-lg font-bold uppercase leading-tight text-leaf-bright">
              A aula está começando agora
            </p>
          ) : (
            units.map(([label, value]) => (
              <div
                key={label}
                className={`min-w-[58px] rounded-2xl border px-2 py-2 text-center sm:min-w-[68px] ${
                  cd.urgent
                    ? "border-gold/40 bg-gold/10"
                    : "border-cream/10 bg-cream/5"
                }`}
              >
                <div
                  key={value}
                  className={`tick-pop font-display text-2xl font-bold tabular-nums sm:text-3xl ${
                    cd.urgent ? "text-lime" : "text-cream"
                  }`}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {cd.ready ? pad(value) : "--"}
                </div>
                <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-cream/50">
                  {label}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


function SuccessState() {
  return (
    <div className="flex flex-col items-center gap-5 px-6 py-12 text-center sm:px-8">
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-leaf-bright/30 blur-2xl" />
        <CheckCircle2
          className="relative size-16 text-leaf-bright"
          strokeWidth={1.5}
        />
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
        Inscrição confirmada
      </p>
      <h1 className="font-display text-3xl font-bold uppercase leading-tight text-cream">
        Te espero ao vivo
      </h1>
      <p className="max-w-[280px] text-sm leading-relaxed text-muted-foreground">
        Sua vaga na masterclass <span className="text-leaf-bright">Cura pela Alimentação</span> está garantida.
        Nos encontramos na <span className="text-cream font-semibold">sábado, às 20h</span>.
      </p>
      <span className="mt-2 inline-flex items-center gap-2 rounded-full border border-leaf/40 bg-leaf/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-cream">
        <span className="size-2 rounded-full bg-leaf-bright live-dot" />
        Ao vivo • {getCurrentEventLabel()}
      </span>
    </div>
  );
}
